import { isTruthy } from '@ardoq/common-helpers';
import { isEqual, omit } from 'lodash';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';
import {
  filterRouteActive,
  isRouteActive,
} from 'router/declarative/filterRouteActive';
import {
  AppLocation,
  AppRouterLocation,
  location$,
} from 'router/declarative/location$';
import { getSearchParam } from 'router/declarative/locationParams';
import { DashboardModule } from '../types';
import { DashboardDataRequest, DashboardModuleRoute } from './types';

export const DASHBOARD_ROUTE = '/dashboard';
export const DASHBOARD_READER_ROUTE = '/dashboard/reader/:dashboardId';

export type DashboardLocation = AppLocation<typeof DASHBOARD_ROUTE>;

/** A location as `location$` emits it, carrying the history action with it. */
type NavigatedLocation = AppRouterLocation & { action: 'push' | 'replace' };

const toDashboardModuleRoute = (
  location: DashboardLocation
): DashboardModuleRoute => {
  switch (location.route) {
    case '/dashboard/builder':
      return {
        dashboardModule: DashboardModule.BUILDER,
        selectedDashboardId: undefined,
      };
    case '/dashboard/builder/:dashboardId':
      return {
        dashboardModule: DashboardModule.BUILDER,
        selectedDashboardId: location.params.dashboardId,
      };
    case '/dashboard/reader/:dashboardId':
      return {
        dashboardModule: DashboardModule.READER,
        selectedDashboardId: location.params.dashboardId,
      };
    case '/dashboard/color-theme-settings':
      return {
        dashboardModule: DashboardModule.COLOR_THEME_SETTINGS,
        selectedDashboardId: undefined,
      };
    case '/dashboard/usage-metrics':
      return {
        dashboardModule: DashboardModule.USAGE_METRICS,
        selectedDashboardId: undefined,
      };
    default:
      return {
        dashboardModule: DashboardModule.OVERVIEW,
        selectedDashboardId: undefined,
      };
  }
};

const toDashboardDataRequest = (
  location: DashboardLocation
): DashboardDataRequest => ({
  ...toDashboardModuleRoute(location),
  stateId: getSearchParam(location, 'state'),
  presentationId: getSearchParam(location, 'presentationId'),
  globalFilterValues: getSearchParam(location, 'dashboardFilterValues'),
  folderId: getSearchParam(location, 'dashboardFolderId'),
});

/**
 * Deduped because consumers treat an emission as a navigation: it resets the
 * selected scenario branch. The presentation and selected-slide write-backs
 * replace the history entry without changing the route, and dropping the branch
 * for that would silently undo the user's choice.
 */
export const dashboardRouteNavigation$: Observable<DashboardModuleRoute> =
  location$.pipe(
    filterRouteActive(DASHBOARD_ROUTE),
    map(toDashboardModuleRoute),
    distinctUntilChanged(isEqual)
  );

/**
 * `presentationId` rides along for the fetch but is left out of the comparison:
 * opening or closing the presentation editor rewrites the param through
 * `reflectPresentationInUrl`, and refetching the dashboard for that would only
 * reload the same data behind a spinner.
 */
const isSameDataRequest = (
  previous: DashboardDataRequest,
  current: DashboardDataRequest
): boolean =>
  isEqual(omit(previous, 'presentationId'), omit(current, 'presentationId'));

/**
 * A data request paired with the history action that produced it. `null` marks a
 * location outside the dashboard routes.
 */
type DashboardDataNavigation = {
  action: 'push' | 'replace';
  request: DashboardDataRequest;
};

const toDashboardDataNavigation = (
  location: NavigatedLocation
): DashboardDataNavigation | null =>
  isRouteActive(location, DASHBOARD_ROUTE)
    ? { action: location.action, request: toDashboardDataRequest(location) }
    : null;

const toDataRequest = ({
  request,
}: DashboardDataNavigation): DashboardDataRequest => request;

/**
 * Only the write-backs are suppressed, and they arrive as a `replace` on the
 * location the user is already on. A `push` is a user-initiated navigation and a
 * `null` is another route taking over, so an identical request after either one
 * still loads: the streams holding the loaded dashboard are ref-counted, so
 * re-opening the same dashboard has to fetch it again.
 */
const isRedundantWriteBack = (
  previous: DashboardDataNavigation | null,
  current: DashboardDataNavigation | null
): boolean =>
  previous !== null &&
  current?.action === 'replace' &&
  isSameDataRequest(previous.request, current.request);

/** Emits when something the dashboard data load depends on changes. */
export const dashboardDataRequest$: Observable<DashboardDataRequest> =
  location$.pipe(
    map(toDashboardDataNavigation),
    distinctUntilChanged(isRedundantWriteBack),
    filter(isTruthy),
    map(toDataRequest)
  );
